<template>
  <div v-if="workspaceOn" class="dash-page ws-page">
    <PaWorkspace
      ref="workspaceRef"
      :order="orderInfo && orderInfo.orderKey ? orderInfo : null"
      :loading="preloader"
      :viewer-mode="lockViewerMode"
      :viewer-text="lockBannerText"
      :can-take-over="lockFree"
      :attention="attentionItems"
      :actions="formActions"
      :since="lastVisit"
      :save-state="saveState"
      @back="backToList"
      @send-plan="workspaceSendToPlan"
      @send-prescriber="allFormStepsRef?.sendToPrescriber()"
      @save-draft="workspaceSaveDraft"
      @archive="showArchiveDialog = true"
      @unarchive="showUnarchiveDialog = true"
      @classic="switchView('classic')"
      @take-over="takeOverEditing"
      @reveal-question="revealCitedQuestion">
      <template #banners>
        <CmmImportBanners
          :reimport="showReimportBanner"
          :importing="showImportInProgressBanner"
          :disabled="lockViewerMode"
          @reimport="goToReimport" />
        <v-alert
          v-for="error in errors"
          :key="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
          closable
          @click:close="errors = errors.filter((e) => e !== error)">
          {{ error }}
        </v-alert>
      </template>
      <template v-if="(showFaxEditor || showEmailEditor) && !lockViewerMode && orderInfo" #outgoing>
        <v-btn-toggle
          v-if="sendEmailEnabled && orderInfo?.submissionMethod !== 'eFax'"
          v-model="sendMethod"
          mandatory
          class="mb-4"
          color="primary"
          variant="outlined">
          <v-btn value="fax">
            <v-icon start>mdi-fax</v-icon>
            Send Fax
          </v-btn>
          <v-btn value="email">
            <v-icon start>mdi-email</v-icon>
            Send Email
          </v-btn>
        </v-btn-toggle>
        <OutgoingFaxEditor
          v-if="sendMethod === 'fax'"
          ref="outgoingFaxEditorRef"
          :types="[]"
          :order-info="orderInfo"
          :to-fax-number="currentFaxNumber"
          :attachment-options="attachmentOptions"
          :frontend-template="currentTemplate"
          :preselected-attachment="preselectedFilledFormAttachment"
          :send-button-text="sendButtonText"
          :defer-send="currentFaxAction === 'prescriber'"
          :supporting-documents="supportingDocuments"
          :attach-supporting-docs-fax-enabled="attachSupportingDocsFaxEnabled"
          title="Send Fax"
          @on-complete="onFaxComplete"
          @on-pre-send="saveOrderDraft"
          @on-signature-redraw-required="focusSignatureSection"
          @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        <OutgoingEmailEditor
          v-if="sendMethod === 'email'"
          ref="outgoingEmailEditorRef"
          :types="[]"
          :order-info="orderInfo"
          :to-emails="currentEmailAddresses"
          :default-subject="currentEmailSubject"
          :attachment-options="attachmentOptions"
          :frontend-template="currentTemplate"
          :preselected-attachment="preselectedFilledFormAttachment"
          :send-button-text="emailSendButtonText"
          :defer-send="currentFaxAction === 'prescriber'"
          :supporting-documents="supportingDocuments"
          title="Send Email"
          @on-complete="onEmailComplete"
          @on-pre-send="saveOrderDraft"
          @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        <v-alert v-if="showFaxHelpText && sendMethod === 'fax'" type="info" variant="tonal" class="mt-3">
          Please verify this is the correct fax number for the insurance provider where the prior authorization will be
          sent.
        </v-alert>
      </template>
      <template #details>
        <div class="submission-status-wrapper ws-details">
          <div class="lh--available-orders-container ws-details__card">
            <SubmissionDetailCard
              :order="orderInfo"
              :buttons-visible="false"
              :readonly="isProvider"
              :fax-forms="faxDestinationOptions" />
          </div>
        </div>
      </template>
      <template #review>
        <PolicyReviewCard :order="orderInfo" />
        <div id="ws-drug-indication">
          <DrugIndicationCard :order="orderInfo" :readonly="isProvider" />
        </div>
      </template>
      <template #form="{ attentionTarget }">
        <AllFormStepsView
          ref="allFormStepsRef"
          workspace
          :attention-target="attentionTarget"
          :navigate-after-submit="false"
          :class="{ 'lh-readonly-fields': lockViewerMode }"
          :disabled="isDisabled || lockViewerMode"
          :filled-form-pdf-blob="filledFormPdfBlob"
          :archivable="!lockViewerMode"
          :actions-visible="!isProvider"
          :viewer-mode="lockViewerMode"
          @send-to-prescriber="handleSendToPrescriber"
          @send-to-plan="handleSendToPlan"
          @send-to-plan-requested="workspaceSendToPlan"
          @appeal-sent="onAppealSent"
          @archive="showArchiveDialog = true"
          @unarchive="showUnarchiveDialog = true"
          @submitted="onWorkspaceSubmitted"
          @draft-saved="markClean"
          @attention="(items) => (attentionItems = items)"
          @reviewed="dirty = true" />
      </template>
      <template #owners="{ placement }">
        <div class="ws-owners" :inert="lockViewerMode">
          <IntakeTagBar
            :tags="orderInfo.statusTags || []"
            :tag-defs="paAvailableTags"
            :tag-due-dates="orderInfo.tagDueDates || []"
            :sort-order="tagSortOrder"
            entity-type="submission"
            :entity-id="orderInfo.orderKey"
            @update:tags="(t) => (orderInfo.statusTags = t)"
            @saved="auditLogListRef?.showLatest()"
            @update:tag-due-dates="(d) => (orderInfo.tagDueDates = d)" />
          <AssigneeBar
            :assignees="orderInfo.assignees || []"
            entity-type="submission"
            :entity-id="orderInfo.orderKey"
            @update:assignees="(a) => (orderInfo.assignees = a)" />
          <GroupBar
            :assigned-groups="orderInfo.assignedGroups || []"
            entity-type="submission"
            :entity-id="orderInfo.orderKey"
            @update:assigned-groups="(g) => (orderInfo.assignedGroups = g)" />
          <SubstatusBar v-if="placement === 'top'" entity-type="submission" :entity-id="orderInfo.orderKey" />
        </div>
      </template>
      <template #activity>
        <AuditLogList
          ref="auditLogListRef"
          :order-info-id="route.params.id"
          :tag-defs="paAvailableTags"
          @mentions-sent="onMentionsSent" />
      </template>
    </PaWorkspace>

    <ConfirmDialog
      v-model="showArchiveDialog"
      title="Archive Submission"
      message="Are you sure you want to archive this submission?"
      confirm-text="Archive"
      variant="warning"
      show-input
      input-label="Archive reason (optional)"
      :loading="archiveLoading"
      @confirm="onArchive"
      @cancel="showArchiveDialog = false" />
    <ConfirmDialog
      v-model="showUnarchiveDialog"
      title="Unarchive Submission"
      message="Are you sure you want to unarchive this submission?"
      confirm-text="Unarchive"
      :loading="archiveLoading"
      @confirm="onUnarchive"
      @cancel="showUnarchiveDialog = false" />
    <v-snackbar v-model="showSuccessNotification" color="primary" timeout="4000" location="bottom">
      {{ successNotificationMessage }}
    </v-snackbar>
    <v-snackbar v-model="viewNoticeOpen" timeout="6000" location="bottom" color="primary">
      {{ viewNotice }}
      <template #actions>
        <v-btn variant="text" size="small" @click="viewNoticeOpen = false">Got it</v-btn>
      </template>
    </v-snackbar>
  </div>
  <div v-else>
    <v-container>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center justify-space-between">
            <div class="detail-page__back" @click="router.back()">
              <v-icon size="18">mdi-arrow-left</v-icon>
              <span>Back to Submissions</span>
            </div>
            <PaViewSwitch v-if="workspaceEligible" model-value="classic" @update:model-value="switchView" />
          </div>
          <h1 class="text-primary weight-600 pb-4">{{ pageTitle }}</h1>
          <CmmImportBanners
            :reimport="showReimportBanner"
            :importing="showImportInProgressBanner"
            :disabled="lockViewerMode"
            @reimport="goToReimport" />
          <div v-if="preloader">
            <SubmissionPageSkeleton />
          </div>
          <div v-else>
            <ReadOnlyBanner v-if="isReadOnlyDevice" />
            <ReadOnlyBanner v-if="lockViewerMode" icon="mdi-account-lock-outline" :text="lockBannerText">
              <template v-if="lockFree" #action>
                <v-btn size="small" color="primary" variant="flat" @click="takeOverEditing">Take over editing</v-btn>
              </template>
            </ReadOnlyBanner>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <SubmissionDetailCard
                  :order="orderInfo"
                  :buttons-visible="false"
                  :readonly="isProvider"
                  :fax-forms="faxDestinationOptions" />
              </v-col>
            </v-row>
            <v-row class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <PolicyReviewCard :order="orderInfo" />
              </v-col>
            </v-row>
            <v-row v-if="orderInfo" class="submission-status-wrapper">
              <v-col cols="12" class="lh--available-orders-container">
                <DrugIndicationCard :order="orderInfo" :readonly="isProvider" />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="submission-detail-section"
                :class="{ 'lh-readonly-fields': isReadOnlyDevice || lockViewerMode }">
                <AllFormStepsView
                  ref="allFormStepsRef"
                  :disabled="isDisabled || isReadOnlyDevice || lockViewerMode"
                  :filled-form-pdf-blob="filledFormPdfBlob"
                  :archivable="!lockViewerMode"
                  :actions-visible="!isProvider"
                  @send-to-prescriber="handleSendToPrescriber"
                  @send-to-plan="handleSendToPlan"
                  @appeal-sent="onAppealSent"
                  @archive="showArchiveDialog = true"
                  @unarchive="showUnarchiveDialog = true"
                  @draft-saved="markClean" />
              </v-col>
            </v-row>
            <v-row v-if="orderInfo && !isProvider">
              <v-col cols="12" :inert="lockViewerMode">
                <IntakeTagBar
                  :tags="orderInfo.statusTags || []"
                  :tag-defs="paAvailableTags"
                  :tag-due-dates="orderInfo.tagDueDates || []"
                  :sort-order="tagSortOrder"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:tags="(t) => (orderInfo.statusTags = t)"
                  @saved="auditLogListRef?.showLatest()"
                  @update:tag-due-dates="(d) => (orderInfo.tagDueDates = d)" />
                <AssigneeBar
                  :assignees="orderInfo.assignees || []"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:assignees="(a) => (orderInfo.assignees = a)" />
                <GroupBar
                  :assigned-groups="orderInfo.assignedGroups || []"
                  entity-type="submission"
                  :entity-id="orderInfo.orderKey"
                  @update:assigned-groups="(g) => (orderInfo.assignedGroups = g)" />
                <SubstatusBar entity-type="submission" :entity-id="orderInfo.orderKey" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="(showFaxEditor || showEmailEditor) && !isReadOnlyDevice && !lockViewerMode">
        <v-col v-if="orderInfo" cols="12">
          <!-- Send Method Toggle (only shown when email is enabled) -->
          <v-btn-toggle
            v-if="sendEmailEnabled && orderInfo?.submissionMethod !== 'eFax'"
            v-model="sendMethod"
            mandatory
            class="mb-4"
            color="primary"
            variant="outlined">
            <v-btn value="fax">
              <v-icon start>mdi-fax</v-icon>
              Send Fax
            </v-btn>
            <v-btn value="email">
              <v-icon start>mdi-email</v-icon>
              Send Email
            </v-btn>
          </v-btn-toggle>

          <!-- Fax Editor -->
          <OutgoingFaxEditor
            v-if="sendMethod === 'fax'"
            ref="outgoingFaxEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-fax-number="currentFaxNumber"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="sendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            :attach-supporting-docs-fax-enabled="attachSupportingDocsFaxEnabled"
            title="Send Fax"
            @on-complete="onFaxComplete"
            @on-pre-send="saveOrderDraft"
            @on-signature-redraw-required="focusSignatureSection"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />

          <!-- Email Editor -->
          <OutgoingEmailEditor
            v-if="sendMethod === 'email'"
            ref="outgoingEmailEditorRef"
            :types="[]"
            :order-info="orderInfo"
            :to-emails="currentEmailAddresses"
            :default-subject="currentEmailSubject"
            :attachment-options="attachmentOptions"
            :frontend-template="currentTemplate"
            :preselected-attachment="preselectedFilledFormAttachment"
            :send-button-text="emailSendButtonText"
            :defer-send="currentFaxAction === 'prescriber'"
            :supporting-documents="supportingDocuments"
            title="Send Email"
            @on-complete="onEmailComplete"
            @on-pre-send="saveOrderDraft"
            @on-frontend-template-selected="handlePrescriberTemplateSelected" />
        </v-col>
        <v-alert v-if="showFaxHelpText && sendMethod === 'fax'" type="info" variant="tonal" prominent class="mb-4">
          <template #prepend>
            <v-icon size="large">mdi-information</v-icon>
          </template>
          <div class="text-body-1 font-weight-bold">
            Please verify this is the correct fax number for the insurance provider where the prior authorization will
            be sent.
          </div>
        </v-alert>
      </v-row>

      <v-row v-if="!isProvider">
        <v-col cols="12">
          <AuditLogList
            ref="auditLogListRef"
            :order-info-id="route.params.id"
            :tag-defs="paAvailableTags"
            @mentions-sent="onMentionsSent" />
        </v-col>
      </v-row>

      <ConfirmDialog
        v-model="showArchiveDialog"
        title="Archive Submission"
        message="Are you sure you want to archive this submission?"
        confirm-text="Archive"
        variant="warning"
        show-input
        input-label="Archive reason (optional)"
        :loading="archiveLoading"
        @confirm="onArchive"
        @cancel="showArchiveDialog = false" />

      <ConfirmDialog
        v-model="showUnarchiveDialog"
        title="Unarchive Submission"
        message="Are you sure you want to unarchive this submission?"
        confirm-text="Unarchive"
        :loading="archiveLoading"
        @confirm="onUnarchive"
        @cancel="showUnarchiveDialog = false" />

      <v-snackbar v-model="showSuccessNotification" color="success" timeout="5000" location="top">
        {{ successNotificationMessage }}
      </v-snackbar>
      <div v-if="errors.length > 0" class="mt-4">
        <v-alert v-for="error in errors" :key="error" type="error" variant="tonal" class="mb-2">
          {{ error }}
        </v-alert>
      </div>
    </v-container>
    <v-snackbar v-model="viewNoticeOpen" timeout="6000" location="bottom" color="primary">
      {{ viewNotice }}
      <template #actions>
        <v-btn variant="text" size="small" @click="viewNoticeOpen = false">Got it</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup>
import AllFormStepsView from "@/pages/AllFormStepsView";
import SubmissionDetailCard from "@/components/SubmissionDetailCard.vue";
import ReadOnlyBanner from "@/components/dashboard/ReadOnlyBanner.vue";
import { useResponsive } from "@/composables/useResponsive";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import OutgoingEmailEditor from "@/components/OutgoingEmailEditor.vue";
import IntakeTagBar from "@/components/dashboard/IntakeTagBar.vue";
import AssigneeBar from "@/components/dashboard/AssigneeBar.vue";
import GroupBar from "@/components/dashboard/GroupBar.vue";
import SubstatusBar from "@/components/dashboard/SubstatusBar.vue";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog.vue";
import PaWorkspace from "@/components/paWorkspace/PaWorkspace.vue";

import { onBeforeMount, onBeforeUnmount, ref, computed, nextTick, watch } from "vue";
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { storeToRefs } from "pinia";
import { clearOrderInfoStorage, convertDateFormat, formatLockHolderName, tryParseApiErrors } from "@/utils";

import { orderInfoService } from "@/services/orderInfoService";
import { userNotesService } from "@/services/userNotesService";
import { mainServices } from "@/services/mainServices";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useAuthStore } from "@/stores";
import { usePaWorkspaceStore } from "@/stores/paWorkspaceStore";

import PolicyReviewCard from "@/components/PolicyReviewCard.vue";
import PaViewSwitch from "@/components/paWorkspace/PaViewSwitch.vue";
import CmmImportBanners from "@/components/CmmImportBanners.vue";
import DrugIndicationCard from "@/components/DrugIndicationCard.vue";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const authStore = useAuthStore();
// Provider logins (LAM-5263) read this page as a pure viewer: no lock, no staff time, no polling.
const isProvider = computed(() => Boolean(authStore.userInfo?.isProvider));
const route = useRoute();
const router = useRouter();
const { isReadOnlyDevice } = useResponsive();
const workspace = usePaWorkspaceStore();
workspace.bindUser(authStore.userInfo?.pk);
// Batch workspace (LAM-5771) is staff-only and desktop-only; providers and phones keep the classic viewer.
const workspaceEligible = computed(() => !isProvider.value && !isReadOnlyDevice.value);
const workspaceOn = computed(() => workspaceEligible.value && workspace.enabled);

// Client config for feature flags
const clientConfig = ref(null);
const sendEmailEnabled = computed(() => clientConfig.value?.uiConfig?.intake?.sendEmailEnabled ?? false);
const paAvailableTags = computed(() => clientConfig.value?.uiConfig?.priorAuth?.availableTags || []);
const tagSortOrder = computed(() => clientConfig.value?.uiConfig?.tagSortOrder ?? null);

const onAppealSent = () => {
  if (orderInfo.value?.orderKey) loadOrder(orderInfo.value.orderKey);
};
const onMentionsSent = (assigned) => {
  if (!orderInfo.value) return;
  const current = orderInfo.value.assignees || [];
  const ids = new Set(current.map((a) => a.id));
  orderInfo.value.assignees = [...current, ...assigned.filter((a) => !ids.has(a.id))];
};
const attachSupportingDocsFaxEnabled = computed(
  () => clientConfig.value?.uiConfig?.priorAuth?.attachSupportingDocsFaxEnabled ?? false
);

const nonEditableStatuses = [
  "Created",
  "Processing",
  "Sent to prescriber",
  "Sent to plan",
  "No payor response received",
  "Requires more information",
  "Approved",
  "Prior authorization not required",
  "Denied",
  "Archived",
];

const isDisabled = computed(() => {
  if (orderInfo.value?.status) {
    if (orderInfo.value.staffEditOverride) return false;
    // CMM orders in Requires More Information are re-openable (LAM-4515); fax ones stay locked.
    if (orderInfo.value.status === "Requires more information" && orderInfo.value.submissionMethod === "CoverMyMeds") {
      return false;
    }
    return nonEditableStatuses.includes(orderInfo.value.status);
  }
  return false;
});

const showReimportBanner = computed(() => !!orderInfo.value?.orderSubmission?.isDemographicsReimportEligible);
const showImportInProgressBanner = computed(() => !!orderInfo.value?.orderSubmission?.isCmmImportInProgress);

const goToReimport = () => {
  const key = orderInfo.value?.orderSubmission?.portalSubmissionId;
  router.push({ name: "import-cmm-submission", query: key ? { key } : {} });
};

// Opening an existing order starts in the skeleton: the lock acquisition awaited before loadOrder()
// otherwise paints the detail cards empty for its whole round-trip (LAM-5432).
const preloader = ref(!!route.params.id);
const pageTitle = ref("Review Submission");
const faxDestinationOptions = ref([]);
const faxAttachments = ref([]);
const showSuccessNotification = ref(false);
const auditLogListRef = ref(null);
const allFormStepsRef = ref(null);
const workspaceRef = ref(null);
const outgoingFaxEditorRef = ref(null);
const filledFormPdfBlob = ref(null);
const errors = ref([]);
const showFaxEditor = ref(false);
const showEmailEditor = ref(false);
const currentFaxAction = ref(null); // 'prescriber' or 'plan'
const currentFaxNumber = ref(null);
const currentTemplate = ref(null);
const sendButtonText = ref("Send Fax");
const showFaxHelpText = ref(false);
const outgoingEmailEditorRef = ref(null);
const currentEmailAddresses = ref([]);
const currentEmailSubject = ref("");
const sendMethod = ref("fax"); // 'fax' or 'email'
const successNotificationMessage = ref("Fax sent successfully!");
const showArchiveDialog = ref(false);
const showUnarchiveDialog = ref(false);
const archiveLoading = ref(false);

async function onArchive(reason) {
  if (lockViewerMode.value || archiveLoading.value) return;
  const orderKey = orderInfo.value.orderKey;
  archiveLoading.value = true;
  try {
    await orderInfoService.archiveOrder(orderKey);
  } catch (e) {
    errors.value = tryParseApiErrors(e);
    archiveLoading.value = false;
    return;
  }
  showArchiveDialog.value = false;
  successNotificationMessage.value = "Submission archived";
  showSuccessNotification.value = true;
  let noteFailed = false;
  const note = reason?.trim();
  if (note) {
    try {
      await userNotesService.createNote({ orderInfo: orderKey, note, level: "archive_reason" });
    } catch (e) {
      noteFailed = true;
    }
  }
  await loadOrder(orderKey);
  auditLogListRef.value?.showLatest();
  if (noteFailed) errors.value = ["The submission was archived, but saving the archive reason failed."];
  archiveLoading.value = false;
}

async function onUnarchive() {
  if (lockViewerMode.value || archiveLoading.value) return;
  const orderKey = orderInfo.value.orderKey;
  archiveLoading.value = true;
  try {
    await orderInfoService.unarchiveOrder(orderKey);
  } catch (e) {
    errors.value = tryParseApiErrors(e);
    archiveLoading.value = false;
    return;
  }
  showUnarchiveDialog.value = false;
  successNotificationMessage.value = "Submission unarchived";
  showSuccessNotification.value = true;
  await loadOrder(orderKey);
  auditLogListRef.value?.showLatest();
  archiveLoading.value = false;
}

const emailSendButtonText = computed(() => {
  if (currentFaxAction.value === "prescriber") {
    return "Send to prescriber";
  }
  return "Send Email";
});

const attachmentOptions = computed(() => {
  const options = [];

  // Response faxes from faxAttachments
  faxAttachments.value.forEach((a, index) => {
    options.push({
      title: `Response Fax ${index + 1}: ${a.label} ${
        a.timestamp ? "Received on " + convertDateFormat(a.timestamp) : ""
      }`,
      value: {
        type: "response_fax",
        s3_url: a.link,
      },
    });
  });

  // Flattened form (for OrderInfo only)
  if (
    orderInfo.value?.editableSubmissionForm &&
    orderInfo.value?.submissionMethod === "eFax" &&
    orderInfo.value?.orderKey
  ) {
    options.push({
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    });
  }

  return options;
});

const supportingDocuments = computed(() => {
  const urls = [];

  // Patient chart URLs: orderInfo.patientChart[].links[0] (if available)
  if (orderInfo.value?.patientChart && Array.isArray(orderInfo.value.patientChart)) {
    orderInfo.value.patientChart.forEach((chart) => {
      if (chart.links && Array.isArray(chart.links) && chart.links.length > 0) {
        urls.push(chart.links[0]);
      }
    });
  }

  // Medical necessity URLs: orderInfo.medicalNecessity (if available)
  if (orderInfo.value?.medicalNecessity && Array.isArray(orderInfo.value.medicalNecessity)) {
    orderInfo.value.medicalNecessity.forEach((url) => {
      if (url) {
        urls.push(url);
      }
    });
  }

  return urls;
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info"];
const reviewStatuses = ["Requires review", "Review clinical info"];

const pageTitleFor = (status) =>
  incompleteStatuses.includes(status)
    ? "Review Submission and Send to Processing"
    : reviewStatuses.includes(status)
    ? "Review Submission and Send to Plan"
    : "Review Submission";

const fetchPrescriberTemplate = async () => {
  if (!orderInfo.value?.orderKey) return null;
  try {
    const data = await orderInfoService.getPrescriberTemplate(orderInfo.value.orderKey);
    return {
      name: data.name,
      content: data.content || "",
      category: "prescriberTemplates",
      templateId: data.templateId || null,
      variant: data.templateVariant || null,
    };
  } catch {
    return null;
  }
};

const preselectedFilledFormAttachment = ref(null);
let lockInterval = null;
let pollInterval = null;
const lockViewerMode = ref(false);
const lockHolder = ref(null);
const lockFree = ref(false);
const wasEditor = ref(false);

const lockBannerText = computed(() => {
  if (isProvider.value) return "You have view-only access to this submission.";
  if (lockFree.value) return "The record is free — you can take over editing.";
  const name = formatLockHolderName(lockHolder.value);
  return name ? `${name} is editing this record — view only.` : "Another user is editing this record — view only.";
});

const enterViewerMode = (lock) => {
  stopLockInterval();
  lockViewerMode.value = true;
  lockFree.value = false;
  lockHolder.value = lock?.user || null;
  showArchiveDialog.value = false;
  showUnarchiveDialog.value = false;
};

const demoteIfLockRejected = (e) => {
  if (e?.response?.status !== 423) return false;
  enterViewerMode(e.response.data?.lock);
  errors.value = ["This record is now being edited by another user."];
  return true;
};

let openGeneration = 0;
async function openOrder(orderKey) {
  const generation = ++openGeneration;
  const stale = () => generation !== openGeneration || route.params.id !== orderKey;
  clearOrderInfoStorage();
  if (isProvider.value) {
    lockViewerMode.value = true;
  } else {
    try {
      await orderInfoService.acquireOrderLock(orderKey);
      if (stale()) {
        orderInfoService.releaseOrderLock(orderKey).catch(() => {});
        return;
      }
      startLockInterval();
      startStaffTime(orderKey);
      wasEditor.value = true;
    } catch (e) {
      if (stale()) return;
      if (e.response?.status !== 423) {
        errors.value = tryParseApiErrors(e);
        // If the redirect is delayed or canceled, the page must show the error, not a stuck skeleton.
        preloader.value = false;
        router.push(listReturnTarget({ name: "prior-auth" }));
        return;
      }
      enterViewerMode(e.response.data?.lock);
    }
    startPollInterval();
  }
  await loadOrder(orderKey);
}

onBeforeMount(async () => {
  if (route.params.id) {
    openOrder(route.params.id);
  }
  getFaxForms();
  window.addEventListener("beforeunload", onBeforeUnload);
  // Fetch client config for feature flags (a company-less provider has none to fetch)
  if (!isProvider.value) {
    try {
      clientConfig.value = await mainServices.getCurrentUserClientConfig();
    } catch (e) {
      // Suppress - email feature will be disabled if config fails to load
    }
  }
});

async function getFaxForms() {
  faxDestinationOptions.value = await mainServices.getFaxForms();
}

async function loadOrder(orderKey) {
  preloader.value = true;
  errors.value = [];
  attentionItems.value = [];
  cleanSnapshot.value = null;
  try {
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    if (orderKey !== route.params.id) return;
    orderInfo.value = fetchedOrder.orderInfo;
    const visitor = authStore.userInfo?.pk;
    if (visitor != null && visitedKey !== orderKey) {
      visitedKey = orderKey;
      lastVisit.value = workspace.lastVisit(orderKey, visitor);
      workspace.recordVisit(orderKey, visitor);
    }
    faxAttachments.value = fetchedOrder.orderInfo.orderSubmission?.responseDocuments || [];
    pageTitle.value = pageTitleFor(orderInfo.value.status);

    // Fetch filled form PDF if submission is not editable and has editableSubmissionForm
    if (orderInfo.value?.editableSubmissionForm && nonEditableStatuses.includes(orderInfo.value.status)) {
      try {
        const pdfResponse = await orderInfoService.getFilledFormPdf(orderKey);
        filledFormPdfBlob.value = pdfResponse.data;
      } catch (err) {
        // Suppress error - filled form might not be available yet
      }
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
  captureCleanSnapshot();
}

const onBeforeUnload = () => {
  stopPollInterval();
  if (lockViewerMode.value) {
    if (wasEditor.value) updateStaffTime();
    return;
  }
  releaseLockOnUnload();
  updateStaffTime();
};

let leaveSaveFailed = false;

onBeforeRouteLeave(async (to) => {
  if (to.meta.loginRequired && !(await saveIfDirty()) && !leaveSaveFailed) {
    leaveSaveFailed = true;
    viewNotice.value = "Your changes couldn't be saved. Leave again to discard them.";
    viewNoticeOpen.value = true;
    return false;
  }
  stopPollInterval();
  if (lockViewerMode.value) {
    window.removeEventListener("beforeunload", onBeforeUnload);
    if (wasEditor.value) await updateStaffTime();
    return;
  }
  await releaseLock();
  await updateStaffTime();
});

const startStaffTime = (orderKey) => {
  try {
    orderInfoService.startOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const updateStaffTime = async (orderKey = route.params.id, { keepUnloadListener = false } = {}) => {
  try {
    if (!keepUnloadListener) window.removeEventListener("beforeunload", onBeforeUnload);
    await orderInfoService.pushOrderStaffTime(orderKey);
  } catch (e) {
    // Suppress
  }
};

const releaseLock = async (orderKey = route.params.id) => {
  stopLockInterval();
  try {
    await orderInfoService.releaseOrderLock(orderKey);
  } catch (e) {
    // Suppress
  }
};

const releaseLockOnUnload = () => {
  stopLockInterval();
  orderInfoService.releaseOrderLockOnUnload(route.params.id);
};

const startLockInterval = () => {
  if (lockInterval) return;
  lockInterval = setInterval(async () => {
    try {
      await orderInfoService.acquireOrderLock(route.params.id);
    } catch (e) {
      // A 423 renew means the lock lapsed and someone else took over; other errors are transient.
      demoteIfLockRejected(e);
    }
  }, 60000);
};

const stopLockInterval = () => {
  clearInterval(lockInterval);
  lockInterval = null;
};

// One 15-s tick carries both jobs: log refresh for everyone, lock peek for viewers.
const startPollInterval = () => {
  if (pollInterval) return;
  pollInterval = setInterval(onPollTick, 15000);
};

const stopPollInterval = () => {
  clearInterval(pollInterval);
  pollInterval = null;
};

const onPollTick = async () => {
  if (document.hidden) return;
  if (lockViewerMode.value) {
    try {
      const lock = await orderInfoService.getOrderLock(route.params.id);
      if (lock) {
        lockHolder.value = lock.user || null;
        lockFree.value = false;
      } else {
        lockFree.value = true;
      }
    } catch (e) {
      // Transient peek failure — try again next tick
    }
  }
  if (orderInfo.value?.editableSubmissionForm?.status === "Processing") {
    await refreshFormWhenAutofillLands();
  }
  auditLogListRef.value?.refresh();
};

const refreshFormWhenAutofillLands = async () => {
  try {
    const fetched = await orderInfoService.getOrderDetails(route.params.id);
    const form = fetched.orderInfo?.editableSubmissionForm;
    if (!form || form.status === "Processing") return;
    orderInfo.value.editableSubmissionForm = form;
    orderInfo.value.status = fetched.orderInfo.status;
    pageTitle.value = pageTitleFor(orderInfo.value.status);
  } catch (e) {
    // Transient fetch failure — try again next tick
  }
};

const takeOverEditing = async () => {
  try {
    await orderInfoService.acquireOrderLock(route.params.id);
  } catch (e) {
    // Lost the takeover race — stay a viewer with the new holder's name.
    if (e.response?.status === 423) enterViewerMode(e.response.data?.lock);
    else errors.value = ["Could not take over editing — try again."];
    return;
  }
  lockViewerMode.value = false;
  lockFree.value = false;
  lockHolder.value = null;
  errors.value = [];
  startLockInterval();
  if (!wasEditor.value) startStaffTime(route.params.id);
  wasEditor.value = true;
  await loadOrder(route.params.id);
};

const saveOrderDraft = async (resolve, reject, { previewOnly = false } = {}) => {
  try {
    // Previewing only needs the current form edits (signature, checkboxes, fields) persisted so
    // the fax renders from up-to-date data — it must never create an order or advance formStep,
    // both of which are send-only side effects. If there's no order yet, there's nothing to render.
    if (previewOnly) {
      if (!orderInfo.value.orderKey) {
        reject(new Error("Order must be saved before it can be previewed"));
        return;
      }
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
      resolve();
      return;
    }
    // For "Send to plan" action, save orderInfo with formStep="Send to Plan" before sending fax
    if (currentFaxAction.value === "plan") {
      // Ensure PDF form is merged if needed
      if (orderInfo.value.editableSubmissionForm && allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }

      // Save orderInfo with formStep="Send to Plan" (same as performSubmission)
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }

      const orderData = {
        ...orderInfo.value,
        formStep: "Send to Plan",
      };

      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderData);
    } else {
      // For other actions (prescriber), just save draft
      if (allFormStepsRef.value?.saveDraft) {
        await allFormStepsRef.value.saveDraft();
      }
    }
    resolve();
  } catch (error) {
    demoteIfLockRejected(error);
    reject(error);
  }
};

const preselectFilledFormAttachment = () => {
  // Pre-select filled form attachment (used for both prescriber and plan actions)
  if ((orderInfo.value?.editableSubmissionForm && orderInfo.value?.orderKey) || filledFormPdfBlob.value) {
    preselectedFilledFormAttachment.value = {
      title: orderInfo.value?.faxDescription ? "Filled " + orderInfo.value?.faxDescription : "Filled Prior Auth Form",
      value: {
        type: "flattened_form",
        s3_url: null,
      },
    };
  }
};

const handlePrescriberTemplateSelected = () => {
  // When prescriber template is selected, pre-select the attachment if eFax
  if (orderInfo.value?.submissionMethod === "eFax") {
    preselectFilledFormAttachment();
  }
};

const buildPrescriberOutboundFromFax = (requestData) => ({
  method: "fax",
  body: requestData.text || "",
  toFaxNumber: requestData.toFaxNumbers?.[0],
  fileLinks: requestData.fileLinks || [],
  attachment: requestData.attachment || null,
  templateId: currentTemplate.value?.templateId ?? null,
  templateVariant: currentTemplate.value?.variant ?? null,
});

const buildPrescriberOutboundFromEmail = (requestData) => ({
  method: "email",
  body: requestData.body || "",
  toEmails: requestData.toEmails || [],
  subject: requestData.subject || "",
  fileLinks: [],
  attachment: null,
  templateId: currentTemplate.value?.templateId ?? null,
  templateVariant: currentTemplate.value?.variant ?? null,
});

const prescriberQueuedNotificationMessage = (submissionMethod, notifyMethod) => {
  if (submissionMethod === "CoverMyMeds") {
    return "Prescriber will be notified after answers are synced to CoverMyMeds.";
  }
  if (notifyMethod === "email") {
    return "Prescriber will be notified by email shortly.";
  }
  return "Prescriber will be notified by fax shortly.";
};

const queuePrescriberNotification = async (outbound) => {
  try {
    await orderInfoService.sendToPrescriber(orderInfo.value.orderKey, {
      pendingPrescriberOutbound: outbound,
    });
    successNotificationMessage.value = prescriberQueuedNotificationMessage(
      orderInfo.value?.submissionMethod,
      outbound.method
    );
    showSuccessNotification.value = true;
    showFaxEditor.value = false;
    showEmailEditor.value = false;
    await loadOrder(orderInfo.value.orderKey);
    if (auditLogListRef.value) {
      auditLogListRef.value.showLatest();
    }
  } catch (e) {
    if (!demoteIfLockRejected(e)) {
      errors.value = tryParseApiErrors(e);
    }
  }
};

const onOutgoingComplete = async ({ sent, queuePayload }, { buildPrescriberOutbound, successMessage }) => {
  if (!sent) {
    return;
  }
  if (currentFaxAction.value === "prescriber" && queuePayload) {
    await queuePrescriberNotification(buildPrescriberOutbound(queuePayload));
    return;
  }
  successNotificationMessage.value = successMessage;
  showSuccessNotification.value = true;
  if (auditLogListRef.value) {
    auditLogListRef.value.showLatest();
  }
  if (currentFaxAction.value === "plan") {
    errors.value = [];
    if (workspaceOn.value) {
      markSent();
      finishAfterSend(successMessage);
      return;
    }
    clearOrderInfoStorage();
    router.push(listReturnTarget({ name: "all-submissions" }));
    window.scrollTo(0, 0);
  }
};

const onFaxComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromFax,
    successMessage: "Fax sent successfully!",
  });

const onEmailComplete = (payload) =>
  onOutgoingComplete(payload, {
    buildPrescriberOutbound: buildPrescriberOutboundFromEmail,
    successMessage: "Email sent successfully!",
  });

const showFaxEditorForAction = async (action) => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Determine if fax editor should be shown based on submission method and action
  if (action === "plan" && submissionMethod !== "eFax") {
    // For "Send to plan", only show fax editor if submission method is eFax
    return;
  }

  currentFaxAction.value = action;

  if (action === "prescriber") {
    // The prescriber's own login preference (LAM-5263) wins over the order flag when they have set one.
    const prefersEmail =
      orderInfo.value?.prescriberPrefersEmail ?? orderInfo.value?.forwardResponsesToProviderByEmail ?? false;
    sendMethod.value = sendEmailEnabled.value && prefersEmail ? "email" : "fax";
    currentTemplate.value = await fetchPrescriberTemplate();
    // The provider login's confirmed fax wins over the order field (LAM-5263); same for email below.
    currentFaxNumber.value = orderInfo.value?.prescriberEffectiveFax || orderInfo.value?.providerContactFax || "";
    sendButtonText.value = "Send fax to prescriber";
    showFaxHelpText.value = false;

    currentEmailSubject.value = `Prior Authorization Request - ${orderInfo.value?.orderKey}`.trim();
    const effectiveEmail = orderInfo.value?.prescriberEffectiveEmail || orderInfo.value?.providerEmail;
    currentEmailAddresses.value = effectiveEmail ? [effectiveEmail] : [];
  } else if (action === "plan") {
    // "plan" only runs for eFax orders (checked above), and the fax/email toggle is hidden for
    // eFax orders (see template) — so this must always be the fax editor, regardless of whatever
    // sendMethod a prior "prescriber" action on this same page load may have left behind.
    sendMethod.value = "fax";
    // For "plan" action, no template - only filled form attachment
    currentTemplate.value = null;
    currentFaxNumber.value = orderInfo.value?.sendFaxFormTo || "";
    sendButtonText.value = "Send fax to payor";
    showFaxHelpText.value = true;

    currentEmailSubject.value = `Prior Authorization Submission - ${orderInfo.value?.orderKey}`.trim();

    // Pre-select filled form attachment for "Send to plan"
    preselectFilledFormAttachment();
  }

  showFaxEditor.value = true;
  showEmailEditor.value = sendEmailEnabled.value;

  // Wait for next tick to ensure component is rendered
  await nextTick();

  // Scroll to the editor component
  const editorRef = sendMethod.value === "email" ? outgoingEmailEditorRef : outgoingFaxEditorRef;
  if (editorRef.value?.$el) {
    editorRef.value.$el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// The fax was refused because the signer's saved signature would fade out in transmission. Bring the
// signature pad back on screen so they can re-draw and press send again — the fax editor keeps
// whatever they had composed, so nothing is lost by failing here.
const focusSignatureSection = async () => {
  allFormStepsRef.value?.focusSignatureSection?.();
  await nextTick();
  document.getElementById("signature-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const handleSendToPrescriber = async () => {
  await showFaxEditorForAction("prescriber");
};

// ---- Batch workspace (LAM-5771) ----
const attentionItems = ref([]);
const lastVisit = ref(null);
let visitedKey = null;
const cleanSnapshot = ref(null);
const dirty = ref(false);
const autosaving = ref(false);

const VOLATILE_ORDER_FIELDS = new Set([
  "portalPayerQuestionsJson",
  "portalForms",
  "formStep",
  "editableSubmissionForm",
  "statusNotes",
  "orderSubmission",
  "modifiedOn",
  "modifiedBy",
  "assignees",
  "assignedGroups",
  "statusTags",
  "tagDueDates",
  "lock",
  "status",
]);

const REVIEWED_QUESTION_FIELDS = new Set(["payerQuestions", "basicPlusQuestions"]);

function snapshotOrder() {
  if (!orderInfo.value?.orderKey) return null;
  return JSON.stringify(orderInfo.value, (key, value) => {
    if (REVIEWED_QUESTION_FIELDS.has(key))
      return (value || []).map((question) => question?.llmPrediction?.reviewedAt ?? null);
    return VOLATILE_ORDER_FIELDS.has(key) ? undefined : value;
  });
}

let dirtyTimer;

function markClean() {
  clearTimeout(dirtyTimer);
  cleanSnapshot.value = snapshotOrder();
  dirty.value = false;
}

function captureCleanSnapshot() {
  nextTick(markClean);
}

watch(
  orderInfo,
  () => {
    if (!workspaceOn.value || cleanSnapshot.value == null) return;
    clearTimeout(dirtyTimer);
    dirtyTimer = setTimeout(() => (dirty.value = snapshotOrder() !== cleanSnapshot.value), 250);
  },
  { deep: true }
);

const formActions = computed(() => allFormStepsRef.value?.actionState ?? {});
const saveState = computed(() => {
  if (lockViewerMode.value) return "viewing";
  if (autosaving.value || formActions.value.saving) return "saving";
  return dirty.value ? "dirty" : "saved";
});

function hasUnsavedChanges() {
  const drifted = cleanSnapshot.value != null && snapshotOrder() !== cleanSnapshot.value;
  return dirty.value || drifted || Boolean(allFormStepsRef.value?.hasUnmergedPdfEdits?.());
}

let pendingSave = null;

function saveIfDirty({ anyView = false } = {}) {
  if ((!anyView && !workspaceOn.value) || lockViewerMode.value || isDisabled.value) return Promise.resolve(true);
  if (pendingSave) return pendingSave;
  if (submittedKey === route.params.id || !hasUnsavedChanges()) return Promise.resolve(true);
  pendingSave = saveDraftBeforeLeaving().finally(() => (pendingSave = null));
  return pendingSave;
}

async function saveDraftBeforeLeaving() {
  autosaving.value = true;
  try {
    await allFormStepsRef.value?.saveDraft({ rethrow: true });
    return true;
  } catch (e) {
    if (!demoteIfLockRejected(e)) errors.value = ["Your changes could not be saved. Try again before moving on."];
    return false;
  } finally {
    autosaving.value = false;
  }
}

function backToList() {
  const back = router.options.history.state.back;
  if (typeof back === "string" && router.resolve(back).meta.loginRequired) router.back();
  else router.push(listReturnTarget({ name: "prior-auth" }));
}

async function closeOrder(orderKey) {
  stopPollInterval();
  if (lockViewerMode.value) {
    if (wasEditor.value) await updateStaffTime(orderKey, { keepUnloadListener: true });
  } else {
    await releaseLock(orderKey);
    await updateStaffTime(orderKey, { keepUnloadListener: true });
  }
  lockViewerMode.value = false;
  lockHolder.value = null;
  lockFree.value = false;
  wasEditor.value = false;
  showFaxEditor.value = false;
  showEmailEditor.value = false;
  filledFormPdfBlob.value = null;
  preselectedFilledFormAttachment.value = null;
  currentFaxAction.value = null;
}

onBeforeRouteUpdate(async (to, from) => {
  if (to.name !== from.name || to.params.id === from.params.id) return true;
  if (!(await saveIfDirty())) return false;
  leaveSaveFailed = false;
  clearTimeout(dirtyTimer);
  cleanSnapshot.value = null;
  dirty.value = false;
  preloader.value = true;
  await closeOrder(from.params.id);
  return true;
});

watch(
  () => route.params.id,
  (orderKey, previous) => {
    if (!orderKey || !previous || orderKey === previous || route.name !== "check-my-coverage") return;
    openOrder(orderKey);
  }
);

watch(workspaceOn, () => {
  if (route.params.id) loadOrder(route.params.id);
});

const VIEW_NOTICES = {
  classic: "You're on the classic view. Switch back to the new look anytime, top right.",
  new: "Welcome back to the new look. Replay the tour anytime from What's new.",
};
const viewNotice = ref("");
const viewNoticeOpen = ref(false);
watch(viewNoticeOpen, (open) => {
  if (!open) leaveSaveFailed = false;
});

async function switchView(view) {
  if ((view === "new") === workspaceOn.value) return;
  if (!(await saveIfDirty({ anyView: true }))) {
    viewNotice.value = "Your changes couldn't be saved, so the view didn't switch.";
    viewNoticeOpen.value = true;
    return;
  }
  workspace.setView(view);
  viewNotice.value = VIEW_NOTICES[view];
  viewNoticeOpen.value = true;
}

async function workspaceSaveDraft() {
  if (lockViewerMode.value || isDisabled.value) return;
  await allFormStepsRef.value?.requestSaveDraft();
}

let submittedKey = null;
const sending = ref(false);
async function workspaceSendToPlan() {
  const form = allFormStepsRef.value;
  if (!form || sending.value || lockViewerMode.value || form.actionState?.submitDisabled) return;
  const orderKey = route.params.id;
  submittedKey = null;
  sending.value = true;
  try {
    await form.submit();
  } finally {
    sending.value = false;
  }
  await nextTick();
  if (submittedKey === orderKey || route.params.id !== orderKey) return;
  const firstError = document.querySelector(".ws .v-input--error, .ws .form-validation-alert");
  if (firstError) {
    requestAnimationFrame(() => firstError.scrollIntoView({ behavior: "smooth", block: "center" }));
  } else if (form.errors?.length) {
    errors.value = [...form.errors];
  }
}

function markSent() {
  submittedKey = route.params.id;
  clearTimeout(dirtyTimer);
  cleanSnapshot.value = null;
  dirty.value = false;
}

function onWorkspaceSubmitted({ reopened }) {
  markSent();
  if (reopened) {
    finishAfterSend("Submission re-opened.");
    return;
  }
  const name = [orderInfo.value?.patientFirstName, orderInfo.value?.patientLastName].filter(Boolean).join(" ");
  finishAfterSend(name ? `Sent ${name} to plan.` : "Sent to plan.");
}

function finishAfterSend(message) {
  successNotificationMessage.value = message;
  showSuccessNotification.value = true;
  backToList();
}

function revealCitedQuestion(id) {
  allFormStepsRef.value?.revealQuestion(id).then((el) => {
    if (!el) return;
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
    workspaceRef.value?.focusElement(el);
  });
}

onBeforeUnmount(() => {
  openGeneration += 1;
  clearTimeout(dirtyTimer);
  stopLockInterval();
  stopPollInterval();
});

const handleSendToPlan = async () => {
  const submissionMethod = orderInfo.value?.submissionMethod;

  // Only intercept if submission method is eFax
  if (submissionMethod === "eFax") {
    await showFaxEditorForAction("plan");
  }
  // For other submission methods, submission proceeds normally in AllFormStepsView
};
</script>

<style lang="scss" scoped>
.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #5e7898;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 8px;
  transition: color 150ms;

  &:hover {
    color: #052e5e;
  }
}
.ws-page {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

#ws-drug-indication:empty {
  display: none;
}

.ws-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@container ws-pane (max-width: 700px) {
  .ws-details :deep(.lh--order-details-item) {
    width: 100% !important;
    margin-left: 0 !important;
  }
}

.ws-details__card {
  padding: 0 !important;
  margin-top: 0 !important;
  background: transparent !important;
}

.ws-owners {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 20px;

  :deep(.intake-tag-bar),
  :deep(.assignee-bar),
  :deep(.group-bar),
  :deep(.substatus-bar) {
    padding: 4px 0;
    gap: 8px;
  }

  :deep(.intake-tag-bar__label),
  :deep(.assignee-bar__label),
  :deep(.group-bar__label) {
    font-family: $lh_font_mono;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.1em;
    color: $lh_color_blue_gray;
  }
}
</style>
